import type { FunctionComponent } from "react";
import React, { useMemo, useRef } from "react";
import { Box, useBreakpointValue, useColorMode, useTheme } from "@chakra-ui/react";
import Marquee from "react-fast-marquee";
import { StockIndicator } from "screens/landing/tabs/project/projectLandingTileLayouts/components/StockIndicator";
import { useFinancialMarket } from "hooks/useFinancialMarket";
import { useUserPreference } from "hooks";
import { DEFAULT_SIDEBAR_WIDTH } from "screens/landing/components";
import { APP_CONTAINER_WIDTH } from "screens/landing";
import { useSettingContext } from "screens/panels/settings/SettingsProvider";

export const ScrollingTickers: FunctionComponent = () => {
  const tickerData = useFinancialMarket();
  const tickersRef = useRef<HTMLDivElement>(null);
  const { colorMode } = useColorMode();
  const theme = useTheme();
  const sidebarWidth = (useUserPreference("ui_sidebar_width") as number) || (DEFAULT_SIDEBAR_WIDTH as number);
  const { isSideNavigationOpen } = useSettingContext();
  const gradientColor = useMemo(() => {
    return colorMode === "dark" ? theme.colors.gray[800] : "#fbfbfb";
  }, [colorMode, theme.colors.gray]);
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
  const isTablet = useBreakpointValue({ base: false, md: true, lg: false }, { fallback: "md", ssr: false });
  const showFullWidth = useUserPreference("ui_enable_view_full_width") as boolean;

  const stockIndicators = useMemo(
    () =>
      tickerData.map((ticker, index) => (
        <Box className="ch-scrolling-stock-ticker" key={`stock-ticker-${index}`} px=".5rem">
          <StockIndicator
            style={{ cursor: "default" }}
            companyTicker={ticker.stock}
            companyStockTitle={`${ticker.name}: ${ticker.stock.replace(".", "").split(":", 1)}`}
            companyStockExchange={ticker.name}
            companyStockPrice={ticker.price}
            companyStockPercentageChange={ticker.percent_change}
            companyStockMovement={ticker.movement}
            companyStockValueChange={ticker.value_change}
          />
        </Box>
      )),
    [tickerData]
  );
  if (!tickerData.length) return null;

  return (
    <Box
      ref={tickersRef}
      px={showFullWidth ? "1rem" : "0"}
      width={
        isMobile || isTablet || showFullWidth
          ? "100vw"
          : !isSideNavigationOpen
          ? `${APP_CONTAINER_WIDTH}px`
          : `calc(100vw - ${sidebarWidth}px)`
      }>
      <Marquee speed={25} pauseOnHover autoFill gradient gradientColor={gradientColor}>
        {stockIndicators}
      </Marquee>
    </Box>
  );
};
